import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Star, SealCheck, Quotes } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export const Stars = ({ value, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5 align-middle">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} weight={value >= n ? "fill" : "regular"} className={value >= n ? "text-primary" : "text-foreground/20"} />
    ))}
  </span>
);

export function ReadersWall() {
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/reviews")).data });
  const [showAll, setShowAll] = useState(false);

  if (!reviews.length) return null;
  const shown = showAll ? reviews : reviews.slice(0, 6);
  const rated = reviews.filter((r) => r.rating);
  const avg = rated.length ? (rated.reduce((s, r) => s + r.rating, 0) / rated.length).toFixed(1) : null;

  return (
    <section id="readers" className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 md:py-24" data-testid="readers-wall">
      <div className="mb-12 flex flex-wrap items-end justify-between gap-4 border-b border-foreground/15 pb-6">
        <div>
          <p className="eyebrow">Readers</p>
          <h2 className="mt-2 font-display text-base font-medium sm:text-lg">What the poems left behind.</h2>
        </div>
        {avg && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="readers-wall-average">
            <Stars value={Math.round(avg)} size={16} /> <span className="font-semibold text-foreground">{avg}</span> · {reviews.length} {reviews.length === 1 ? "note" : "notes"}
          </p>
        )}
      </div>

      <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [&>*]:mb-6">
        {shown.map((r, i) => (
          <motion.figure
            key={r.review_id}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: Math.min(i, 5) * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="break-inside-avoid rounded-sm border border-foreground/10 bg-white p-6"
            data-testid={`review-card-${r.review_id}`}
          >
            <Quotes size={20} weight="fill" className="text-primary/35" />
            {r.rating ? <div className="mt-3"><Stars value={r.rating} /></div> : null}
            <blockquote className="mt-3 text-[15px] leading-[1.75] text-foreground/85">{r.body}</blockquote>
            {r.favourite_poem_ta && (
              <p className="mt-4 font-display text-sm text-primary">Favourite: {r.favourite_poem_ta}</p>
            )}
            <figcaption className="mt-5 flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
              <span className="font-semibold text-foreground">{r.name}</span>
              {r.city && <span>· {r.city}</span>}
              {r.verified && <span className="inline-flex items-center gap-1 text-accent" title="Verified buyer"><SealCheck size={14} weight="fill" /> verified</span>}
            </figcaption>
            {r.reply && (
              <p className="mt-4 border-l-2 border-primary/40 pl-3 text-sm italic text-foreground/70">{r.reply} <span className="not-italic">— Shunmugavel</span></p>
            )}
          </motion.figure>
        ))}
      </div>

      {reviews.length > 6 && !showAll && (
        <button onClick={() => setShowAll(true)} className="mt-4 border-b border-foreground/25 pb-1 text-sm font-semibold transition-colors hover:border-primary hover:text-primary" data-testid="readers-wall-show-all">
          Read all {reviews.length} notes
        </button>
      )}
    </section>
  );
}
